import { useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useQuery, useMutation, useSubscription } from "urql"
import { Pencil, Plus, Trash2, FastForward, Play, Download, ChevronDown, ChevronUp } from "lucide-react"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { PageHeader } from "@/components/shared/page-header"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { StreamForm } from "../components/stream-form"
import { ConsumerForm } from "../components/consumer-form"
import { SinkForm } from "../components/sink-form"
import {
  StreamQuery,
  StreamEntriesQuery,
  ConsumeStreamQuery,
  DeleteStreamConsumerMutation,
  DeleteStreamSinkMutation,
  AdvanceConsumerCursorMutation,
  StreamEntryAppendedSubscription,
} from "@/lib/graphql/streaming"
import { useAuth } from "@/hooks/use-auth"
import { exportJSON, exportCSV } from "@/lib/export"
import type { ResultOf } from "@/lib/graphql/types"

type StreamData = NonNullable<ResultOf<typeof StreamQuery>["stream"]>
type EntryItem = StreamData["entries"]["nodes"][number]
type ConsumerItem = StreamData["consumers"]["nodes"][number]
type SinkItem = StreamData["sinks"]["nodes"][number]

const entryColumns: Column<EntryItem>[] = [
  {
    header: "Seq",
    cell: (row) => <span className="text-xs font-mono tabular-nums">{row.sequence}</span>,
  },
  {
    header: "Event Type",
    cell: (row) => (
      <code className="text-xs font-mono text-muted-foreground">{row.eventType || "-"}</code>
    ),
  },
  {
    header: "Message",
    cell: (row) => row.messageId ? (
      <code className="text-xs font-mono text-muted-foreground max-w-30 truncate block">
        {row.messageId.slice(0, 8)}
      </code>
    ) : <span className="text-xs text-muted-foreground">-</span>,
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

const consumerColumns: Column<ConsumerItem>[] = [
  {
    header: "Name",
    cell: (row) => <span className="font-medium text-sm">{row.name}</span>,
  },
  {
    header: "Cursor",
    cell: (row) => <span className="text-xs font-mono tabular-nums">{row.cursorSequence}</span>,
  },
  {
    header: "Updated",
    cell: (row) => <TimeAgo date={row.updatedAt} />,
  },
]

const sinkTypeLabels: Record<string, string> = {
  S3: "S3",
  WEBHOOK: "Webhook",
  POSTGRES: "Postgres",
}

export function StreamDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isConfigured } = useAuth()
  const [result, reexecute] = useQuery({ query: StreamQuery, variables: { id: id! } })

  const handleNewEntry = useCallback(
    (
      _prev: ResultOf<typeof StreamEntryAppendedSubscription> | undefined,
      response: ResultOf<typeof StreamEntryAppendedSubscription>,
    ) => {
      if (response?.streamEntryAppended) {
        reexecute({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecute],
  )

  useSubscription(
    { query: StreamEntryAppendedSubscription, variables: { streamId: id! }, pause: !isConfigured },
    handleNewEntry,
  )
  const [editOpen, setEditOpen] = useState(false)
  const [consumerFormOpen, setConsumerFormOpen] = useState(false)
  const [sinkFormOpen, setSinkFormOpen] = useState(false)
  const [editSink, setEditSink] = useState<SinkItem | null>(null)
  const [deleteConsumerId, setDeleteConsumerId] = useState<string | null>(null)
  const [deleteSinkId, setDeleteSinkId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [advanceConsumer, setAdvanceConsumer] = useState<ConsumerItem | null>(null)
  const [advanceSequence, setAdvanceSequence] = useState("")
  const [advancing, setAdvancing] = useState(false)
  const [consumeConsumer, setConsumeConsumer] = useState<ConsumerItem | null>(null)
  const [entriesAfterSeq, setEntriesAfterSeq] = useState<number | null>(null)
  const [showOlderEntries, setShowOlderEntries] = useState(false)

  const [olderEntriesResult] = useQuery({
    query: StreamEntriesQuery,
    variables: { streamId: id!, afterSequence: entriesAfterSeq ?? 0, limit: 50 },
    pause: !showOlderEntries,
  })
  type OlderEntry = ResultOf<typeof StreamEntriesQuery>["streamEntries"]["nodes"][number]

  const [, deleteConsumer] = useMutation(DeleteStreamConsumerMutation)
  const [, deleteSink] = useMutation(DeleteStreamSinkMutation)
  const [, advanceCursor] = useMutation(AdvanceConsumerCursorMutation)

  const [consumeResult] = useQuery({
    query: ConsumeStreamQuery,
    variables: { streamId: id!, consumerName: consumeConsumer?.name ?? "", limit: 50 },
    pause: !consumeConsumer,
  })
  type ConsumeEntry = ResultOf<typeof ConsumeStreamQuery>["consumeStream"]["nodes"][number]

  const stream = result.data?.stream

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!stream) return <EmptyState title="Stream not found" />

  const sinkColumns: Column<SinkItem>[] = [
    {
      header: "Name",
      cell: (row) => <span className="font-medium text-sm">{row.name}</span>,
    },
    {
      header: "Type",
      cell: (row) => (
        <span className="text-xs text-muted-foreground">{sinkTypeLabels[row.sinkType] ?? row.sinkType}</span>
      ),
    },
    {
      header: "Status",
      cell: (row) => <StatusBadge status={row.status} />,
    },
    {
      header: "Cursor",
      cell: (row) => <span className="text-xs font-mono tabular-nums">{row.cursorSequence}</span>,
    },
    {
      header: "Batch",
      cell: (row) => <span className="text-xs tabular-nums">{row.batchSize}</span>,
    },
    {
      header: "Interval",
      cell: (row) => <span className="text-xs text-muted-foreground">{row.flushInterval}</span>,
    },
    {
      header: "Last Flush",
      cell: (row) => row.lastFlushedAt ? <TimeAgo date={row.lastFlushedAt} /> : <span className="text-xs text-muted-foreground">Never</span>,
    },
    {
      header: "",
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditSink(row) }}>
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={(e) => { e.stopPropagation(); setDeleteSinkId(row.id) }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ),
      className: "w-20 text-right",
    },
  ]

  const handleDeleteConsumer = async () => {
    if (!deleteConsumerId) return
    setDeleting(true)
    const res = await deleteConsumer({ id: deleteConsumerId })
    setDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Consumer deleted")
    setDeleteConsumerId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleDeleteSink = async () => {
    if (!deleteSinkId) return
    setDeleting(true)
    const res = await deleteSink({ id: deleteSinkId })
    setDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Sink deleted")
    setDeleteSinkId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleAdvanceCursor = async () => {
    if (!advanceConsumer) return
    const seq = parseInt(advanceSequence, 10)
    if (isNaN(seq) || seq < 0) {
      toast.error("Enter a valid sequence number")
      return
    }
    setAdvancing(true)
    const res = await advanceCursor({ id: advanceConsumer.id, sequence: seq })
    setAdvancing(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Cursor advanced to ${seq}`)
    setAdvanceConsumer(null)
    setAdvanceSequence("")
    reexecute({ requestPolicy: "network-only" })
  }

  const handleAcknowledgeAll = async () => {
    if (!consumeConsumer || !consumeResult.data) return
    const entries = consumeResult.data.consumeStream.nodes
    if (entries.length === 0) return
    const lastEntry = entries[entries.length - 1]
    if (!lastEntry) return
    const lastSeq = lastEntry.sequence
    setAdvancing(true)
    const res = await advanceCursor({ id: consumeConsumer.id, sequence: lastSeq })
    setAdvancing(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Acknowledged ${entries.length} entries, cursor advanced to ${lastSeq}`)
    setConsumeConsumer(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const olderEntryColumns: Column<OlderEntry>[] = [
    {
      header: "Seq",
      cell: (row) => <span className="text-xs font-mono tabular-nums">{row.sequence}</span>,
    },
    {
      header: "Event Type",
      cell: (row) => <code className="text-xs font-mono text-muted-foreground">{row.eventType || "-"}</code>,
    },
    {
      header: "Message",
      cell: (row) => row.messageId ? (
        <code className="text-xs font-mono text-muted-foreground max-w-30 truncate block">{row.messageId.slice(0, 8)}</code>
      ) : <span className="text-xs text-muted-foreground">-</span>,
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
  ]

  const consumeEntryColumns: Column<ConsumeEntry>[] = [
    {
      header: "Seq",
      cell: (row) => <span className="text-xs font-mono tabular-nums">{row.sequence}</span>,
    },
    {
      header: "Event Type",
      cell: (row) => <code className="text-xs font-mono text-muted-foreground">{row.eventType || "-"}</code>,
    },
    {
      header: "Message",
      cell: (row) => row.messageId ? (
        <code className="text-xs font-mono text-muted-foreground max-w-30 truncate block">{row.messageId.slice(0, 8)}</code>
      ) : <span className="text-xs text-muted-foreground">-</span>,
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
  ]

  const refresh = () => reexecute({ requestPolicy: "network-only" })

  return (
    <div className="space-y-6">
      <PageHeader title={stream.name}>
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
          <Pencil className="h-3.5 w-3.5 mr-1.5" />
          Edit
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <FieldLabel className="mb-1">Name</FieldLabel>
                <div className="font-medium">{stream.name}</div>
              </div>
              <div>
                <FieldLabel className="mb-1">Application</FieldLabel>
                <button
                  type="button"
                  className="text-sm text-primary hover:underline cursor-pointer"
                  onClick={() => navigate(`/applications/${stream.applicationId}`)}
                >
                  {stream.application?.name}
                </button>
              </div>
              <div>
                <FieldLabel className="mb-1">Status</FieldLabel>
                <StatusBadge status={stream.status} />
              </div>
              <div>
                <FieldLabel className="mb-1">Retention</FieldLabel>
                <div className="text-sm tabular-nums">{stream.retentionDays} days</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">ID</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{stream.id}</code>
                <CopyButton text={stream.id} />
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Entries</FieldLabel>
              <div className="tabular-nums">{stream.entries.pageInfo.total}</div>
            </div>
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={stream.createdAt} />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium">Recent Entries ({stream.entries.pageInfo.total})</h2>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="h-3.5 w-3.5 mr-1.5" />Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => {
                  const rows = (stream.entries.nodes).map((e) => ({
                    id: e.id, sequence: e.sequence, eventType: e.eventType,
                    messageId: e.messageId, createdAt: e.createdAt,
                  }))
                  exportJSON(rows, "stream-entries")
                }}>Export JSON</DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  const rows = (stream.entries.nodes).map((e) => ({
                    id: e.id, sequence: e.sequence, eventType: e.eventType,
                    messageId: e.messageId, createdAt: e.createdAt,
                  }))
                  exportCSV(rows, ["id", "sequence", "eventType", "messageId", "createdAt"], "stream-entries")
                }}>Export CSV</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <DataTable
          columns={entryColumns}
          data={stream.entries.nodes}
          keyFn={(row) => row.id}
          emptyTitle="No entries"
          emptyDescription="Entries will appear as messages are published"
        />
        {stream.entries.pageInfo.total > 50 && (
          <div className="space-y-4">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                if (!showOlderEntries) {
                  const lastEntry = stream.entries.nodes[stream.entries.nodes.length - 1]
                  setEntriesAfterSeq(lastEntry ? lastEntry.sequence : 0)
                }
                setShowOlderEntries(!showOlderEntries)
              }}
            >
              {showOlderEntries ? (
                <><ChevronUp className="h-3.5 w-3.5 mr-1.5" />Hide Older Entries</>
              ) : (
                <><ChevronDown className="h-3.5 w-3.5 mr-1.5" />Load Older Entries</>
              )}
            </Button>
            {showOlderEntries && (
              <>
                <DataTable
                  columns={olderEntryColumns}
                  data={olderEntriesResult.data?.streamEntries.nodes ?? []}
                  keyFn={(row) => row.id}
                  loading={olderEntriesResult.fetching}
                  emptyTitle="No more entries"
                  emptyDescription="All entries have been loaded"
                />
                {(olderEntriesResult.data?.streamEntries.nodes.length ?? 0) >= 50 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      const nodes = olderEntriesResult.data?.streamEntries.nodes
                      if (nodes && nodes.length > 0) {
                        const lastNode = nodes[nodes.length - 1]
                        if (lastNode) setEntriesAfterSeq(lastNode.sequence)
                      }
                    }}
                  >
                    Load More...
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium">Consumers ({stream.consumers.pageInfo.total})</h2>
          <Button size="sm" variant="outline" onClick={() => setConsumerFormOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Consumer
          </Button>
        </div>
        <DataTable
          columns={[
            ...consumerColumns,
            {
              header: "",
              cell: (row) => (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setConsumeConsumer(row) }}
                    title="Consume entries"
                  >
                    <Play className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setAdvanceConsumer(row); setAdvanceSequence(String(row.cursorSequence)) }}
                    title="Advance cursor"
                  >
                    <FastForward className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={(e) => { e.stopPropagation(); setDeleteConsumerId(row.id) }}
                    title="Delete consumer"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ),
              className: "w-28 text-right",
            },
          ]}
          data={stream.consumers.nodes}
          keyFn={(row) => row.id}
          emptyTitle="No consumers"
          emptyDescription="Add a consumer to track read progress"
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium">Sinks ({stream.sinks.pageInfo.total})</h2>
          <Button size="sm" variant="outline" onClick={() => setSinkFormOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Sink
          </Button>
        </div>
        <DataTable
          columns={sinkColumns}
          data={stream.sinks.nodes}
          keyFn={(row) => row.id}
          emptyTitle="No sinks"
          emptyDescription="Add a sink to export entries to S3, a webhook, or Postgres"
        />
      </div>

      <StreamForm
        open={editOpen}
        onOpenChange={setEditOpen}
        onSuccess={refresh}
        initialData={stream}
      />

      <ConsumerForm
        open={consumerFormOpen}
        onOpenChange={setConsumerFormOpen}
        onSuccess={refresh}
        streamId={stream.id}
      />

      <SinkForm
        open={sinkFormOpen}
        onOpenChange={(open) => { setSinkFormOpen(open); if (!open) setEditSink(null) }}
        onSuccess={refresh}
        streamId={stream.id}
      />

      <SinkForm
        open={!!editSink}
        onOpenChange={(open) => { if (!open) setEditSink(null) }}
        onSuccess={refresh}
        streamId={stream.id}
        initialData={editSink ?? undefined}
      />

      <ConfirmDialog
        open={!!deleteConsumerId}
        onOpenChange={(open) => { if (!open) setDeleteConsumerId(null) }}
        title="Delete Consumer"
        description="This will permanently delete this consumer and its cursor state."
        confirmLabel="Delete"
        onConfirm={handleDeleteConsumer}
        loading={deleting}
        destructive
      />

      <ConfirmDialog
        open={!!deleteSinkId}
        onOpenChange={(open) => { if (!open) setDeleteSinkId(null) }}
        title="Delete Sink"
        description="This will permanently delete this sink. No more data will be delivered."
        confirmLabel="Delete"
        onConfirm={handleDeleteSink}
        loading={deleting}
        destructive
      />

      <Dialog open={!!advanceConsumer} onOpenChange={(open) => { if (!open) { setAdvanceConsumer(null); setAdvanceSequence("") } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Advance Cursor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg glass-card p-4 space-y-3">
              <div className="text-sm">
                <FieldLabel className="mb-1">Consumer</FieldLabel>
                <div className="font-medium">{advanceConsumer?.name}</div>
              </div>
              <div className="text-sm">
                <FieldLabel className="mb-1">Current Cursor</FieldLabel>
                <div className="font-mono tabular-nums">{advanceConsumer?.cursorSequence}</div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="advance-seq">Target Sequence</Label>
                <Input
                  id="advance-seq"
                  type="number"
                  className="font-mono text-sm"
                  value={advanceSequence}
                  onChange={(e) => setAdvanceSequence(e.target.value)}
                  min={0}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setAdvanceConsumer(null); setAdvanceSequence("") }}>Cancel</Button>
              <Button onClick={handleAdvanceCursor} disabled={advancing}>
                {advancing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Advance"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!consumeConsumer} onOpenChange={(open) => { if (!open) setConsumeConsumer(null) }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Consume: {consumeConsumer?.name}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Entries from cursor position {consumeConsumer?.cursorSequence}
            </p>
          </DialogHeader>
          <DataTable
            columns={consumeEntryColumns}
            data={consumeResult.data?.consumeStream.nodes ?? []}
            keyFn={(row) => row.id}
            loading={consumeResult.fetching}
            emptyTitle="No pending entries"
            emptyDescription="All entries have been consumed"
          />
          {(consumeResult.data?.consumeStream.nodes.length ?? 0) > 0 && (
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setConsumeConsumer(null)}>Close</Button>
              <Button onClick={handleAcknowledgeAll} disabled={advancing}>
                {advancing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Acknowledge All"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
